import React, { createContext, useContext, ReactNode } from "react";
import {
  useGlobalErrorHandler,
  GlobalErrorContext,
} from "../../hooks/useGlobalErrorHandler";

interface GlobalErrorProviderProps {
  children: ReactNode;
}

export const GlobalErrorProvider: React.FC<GlobalErrorProviderProps> = ({
  children,
}) => {
  const errorHandler = useGlobalErrorHandler();

  return (
    <GlobalErrorContext.Provider value={errorHandler}>
      {children}
      <GlobalErrorDisplay />
    </GlobalErrorContext.Provider>
  );
};

// Global error display component
const GlobalErrorDisplay: React.FC = () => {
  const { errors, removeError, retryError } = useGlobalErrorHandler();

  if (errors.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {errors.map((error) => (
        <div
          key={error.id}
          className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg"
          role="alert"
          aria-live="polite"
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <span className="text-red-400 text-lg">⚠️</span>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">
                {error.context ? `${error.context} Error` : "Error"}
              </h3>
              <p className="text-sm text-red-700 mt-1">{error.message}</p>
              <div className="mt-2 flex space-x-2">
                {error.retryable && error.retryCount < error.maxRetries && (
                  <button
                    onClick={() => retryError(error.id)}
                    className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 transition-colors"
                    aria-label={`Retry (attempt ${error.retryCount + 1}/${
                      error.maxRetries
                    })`}
                  >
                    Retry ({error.retryCount + 1}/{error.maxRetries})
                  </button>
                )}
                <button
                  onClick={() => removeError(error.id)}
                  className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded hover:bg-gray-300 transition-colors"
                  aria-label="Dismiss error"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Hook to use global error handler
export const useGlobalError = () => {
  const context = useContext(GlobalErrorContext);
  if (!context) {
    throw new Error("useGlobalError must be used within a GlobalErrorProvider");
  }
  return context;
};
